import { create } from 'zustand';
import type { Conversation, ChatMessage } from '@/types/chat/chat.types';

interface ChatState {
  conversations: Conversation[];
  activeConversationId: string | null;
  messages: Record<string, ChatMessage[]>; // conversationId -> messages
  chatOpen: boolean;

  setConversations: (list: Conversation[]) => void;
  setActiveConversation: (id: string | null) => void;
  setMessages: (conversationId: string, msgs: ChatMessage[]) => void;
  addMessage: (conversationId: string, msg: ChatMessage) => void;
  setChatOpen: (open: boolean) => void;
  toggleChat: () => void;
}

export const useChatStore = create<ChatState>()((set, get) => ({
  conversations: [],
  activeConversationId: null,
  messages: {},
  chatOpen: false,

  setConversations: (list) => set({ conversations: list }),

  setActiveConversation: (id) => set({ activeConversationId: id }),

  setMessages: (conversationId, msgs) =>
    set((s) => ({
      messages: { ...s.messages, [conversationId]: msgs },
    })),

  addMessage: (conversationId, msg) =>
    set((s) => {
      const existing = s.messages[conversationId] || [];
      // Avoid duplicates
      if (existing.some((m) => m.id === msg.id)) return s;
      return {
        messages: {
          ...s.messages,
          [conversationId]: [...existing, msg],
        },
        // Also update lastMessage in conversations
        conversations: s.conversations.map((c) =>
          c.id === conversationId ? { ...c, lastMessage: msg } : c,
        ),
      };
    }),

  setChatOpen: (open) => set({ chatOpen: open }),
  toggleChat: () => set((s) => ({ chatOpen: !s.chatOpen })),
}));
